// src/components/TeamLeadDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingApprovals, approveProperty, rejectProperty, requestChanges } from '../api';

function TeamLeadDashboard() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('role');

  const [properties, setProperties] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Request Changes modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [changeComment, setChangeComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: 'pending',
    city: '',
    sub_locality: '',
    propertyCategory: '',
    minCarpet: '',
    maxCarpet: '',
    search: '',
  });

  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError('');

      const params = { page, limit: 20 };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value;
        }
      });

      const response = await getPendingApprovals(params);
      setProperties(response.properties || []);
      setPagination(response.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to load properties';
      setError(message);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchProperties();
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'pending',
      city: '',
      sub_locality: '',
      propertyCategory: '',
      minCarpet: '',
      maxCarpet: '',
      search: '',
    });
    setPage(1);
  };

  const handleApprove = async (id, location) => {
    if (!window.confirm(`Approve "${location}"?`)) return;

    try {
      setError('');
      await approveProperty(id);
      setSuccessMessage(`✅ Approved: ${location}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleReject = async (id, location) => {
    if (!window.confirm(`Reject "${location}"? This is final.`)) return;

    try {
      setError('');
      await rejectProperty(id);
      setSuccessMessage(`❌ Rejected: ${location}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject');
    }
  };

  const handleOpenRequestChanges = (property) => {
    setSelectedProperty(property);
    setChangeComment('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProperty(null);
    setChangeComment('');
  };

  const handleSubmitRequestChanges = async () => {
    if (!changeComment.trim()) {
      setError('Please provide a comment explaining what changes are needed');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await requestChanges(selectedProperty._id, changeComment.trim());
      setSuccessMessage(`📝 Changes requested for: ${selectedProperty.location}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      handleCloseModal();
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request changes');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      changes_requested: 'bg-orange-100 text-orange-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const timeAgo = (dateString) => {
    if (!dateString) return '';
    const days = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">⏳ Pending Approvals</h1>
          <p className="text-sm text-gray-500 mt-1">
            {userRole === 'admin' && 'All pending properties across all cities'}
            {userRole === 'team_lead' && 'Pending properties in your assigned cities'}
          </p>
        </div>
        <div className="text-sm text-gray-600">
          Pending: <span className="font-bold text-yellow-600">{pagination.total}</span>
        </div>
      </div>

      {/* Success/Error messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded mb-4">
          {successMessage}
        </div>
      )}
      {error && !showModal && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <h3 className="font-bold text-gray-700 mb-3">🔍 Filters</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="border rounded p-2 w-full text-sm"
            >
              <option value="pending">Pending</option>
              <option value="changes_requested">Changes Requested</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">City</label>
            <select
              name="city"
              value={filters.city}
              onChange={handleFilterChange}
              className="border rounded p-2 w-full text-sm"
            >
              <option value="">All Cities</option>
              <option value="mumbai">Mumbai</option>
              <option value="pune">Pune</option>
              <option value="delhi">Delhi</option>
              <option value="bangalore">Bangalore</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Micromarket</label>
            <input
              type="text"
              name="sub_locality"
              value={filters.sub_locality}
              onChange={handleFilterChange}
              placeholder="e.g., Andheri East"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Type</label>
            <select
              name="propertyCategory"
              value={filters.propertyCategory}
              onChange={handleFilterChange}
              className="border rounded p-2 w-full text-sm"
            >
              <option value="">All Types</option>
              <option value="office">Office</option>
              <option value="warehouse">Warehouse</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Min Carpet (sqft)</label>
            <input
              type="number"
              name="minCarpet"
              value={filters.minCarpet}
              onChange={handleFilterChange}
              placeholder="e.g., 5000"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Max Carpet (sqft)</label>
            <input
              type="number"
              name="maxCarpet"
              value={filters.maxCarpet}
              onChange={handleFilterChange}
              placeholder="e.g., 15000"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Location, building name..."
              className="border rounded p-2 w-full text-sm"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <button
            onClick={handleApplyFilters}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded text-sm"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm"
          >
            Clear
          </button>
          <button
            onClick={fetchProperties}
            className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded text-sm"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Property List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-gray-500 text-lg">🎉 No pending properties</p>
          <p className="text-gray-400 text-sm mt-2">All caught up!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {properties.map((property) => (
            <div
              key={property._id}
              className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow border-l-4 border-yellow-400"
            >
              <div className="flex flex-col md:flex-row md:justify-between gap-3">
                {/* Left: Property Info */}
                <div className="flex-1">
                  <div className="flex items-start gap-2 mb-2 flex-wrap">
                    <h3 className="font-bold text-lg text-gray-800">
                      {property.location || 'Untitled'}
                    </h3>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(property.status)}`}>
                      {property.status}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-1">
                    📍 {property.sub_locality || 'N/A'}, {property.city || 'N/A'}
                  </p>

                  <div className="flex flex-wrap gap-3 text-sm text-gray-700 mt-2">
                    <span>🏢 {property.property_type || 'N/A'}</span>
                    {property.rent_per_sqft && property.rent_per_sqft !== 'N/A' && (
                      <span>💰 {property.rent_per_sqft}</span>
                    )}
                    {property.price && property.price !== 'Price on Request' && (
                      <span>💵 {property.price}</span>
                    )}
                    {property.carpet_area && property.carpet_area !== 'N/A' && (
                      <span>📐 {property.carpet_area}</span>
                    )}
                    {property.floor && property.floor !== 'N/A' && (
                      <span>🏗️ {property.floor}</span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-2">
                    {property.createdByEmail && property.createdByEmail !== 'N/A' && (
                      <span>👤 {property.createdByEmail}</span>
                    )}
                    {property.created_at && (
                      <span>🕐 {timeAgo(property.created_at)}</span>
                    )}
                    {property.createdByName && property.createdByName === 'Legacy Import' && (
                      <span className="text-orange-600">📦 Legacy</span>
                    )}
                  </div>

                  {/* Show changes request comment if applicable */}
                  {property.status === 'changes_requested' && property.changesRequestComment && (
                    <div className="bg-orange-50 border border-orange-200 rounded p-2 mt-2 text-sm">
                      <span className="font-bold text-orange-700">📝 Changes requested:</span>{' '}
                      {property.changesRequestComment}
                    </div>
                  )}
                </div>

                {/* Right: Action Buttons */}
                <div className="flex flex-wrap gap-2 md:flex-col md:items-end md:min-w-[160px]">
                    <button
                        onClick={() => navigate(`/property/${property._id}`)}
                        className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded"
                    >
                        👁️ View
                    </button>

                  <button
                    onClick={() => navigate(`/edit-property/${property._id}`)}
                    className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1.5 rounded"
                  >
                    ✏️ Edit
                  </button>

                  <button
                    onClick={() => handleApprove(property._id, property.location)}
                    className="text-xs bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded font-medium"
                  >
                    ✅ Approve
                  </button>

                  <button
                    onClick={() => handleOpenRequestChanges(property)}
                    className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded font-medium"
                  >
                    📝 Request Changes
                  </button>

                  <button
                    onClick={() => handleReject(property._id, property.location)}
                    className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded font-medium"
                  >
                    ❌ Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-4 py-2 bg-white border rounded disabled:opacity-50 hover:bg-gray-50"
          >
            ← Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(Math.min(pagination.pages, page + 1))}
            disabled={page >= pagination.pages}
            className="px-4 py-2 bg-white border rounded disabled:opacity-50 hover:bg-gray-50"
          >
            Next →
          </button>
        </div>
      )}

      {/* Request Changes Modal */}
      {showModal && selectedProperty && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-xl">
            <h3 className="text-xl font-bold mb-3">📝 Request Changes</h3>
            <p className="text-sm text-gray-600 mb-4">
              Property: <span className="font-bold">{selectedProperty.location}</span>
            </p>

            <label className="block text-sm font-medium mb-1">
              What changes are needed? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={changeComment}
              onChange={(e) => setChangeComment(e.target.value)}
              placeholder="e.g., Please update the carpet area and add parking details"
              className="border rounded p-2 w-full text-sm"
              rows="4"
              autoFocus
            />

            {error && (
              <p className="text-red-600 text-sm mt-2">{error}</p>
            )}

            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={handleCloseModal}
                disabled={submitting}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRequestChanges}
                disabled={submitting || !changeComment.trim()}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamLeadDashboard;